import { useEffect, useState } from "react";
import { createTrainerCourse, fetchCategories } from "@/api/trainerAuth";

export default function CreateCourse() {
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [imagePreview, setImagePreview] = useState(null);

  const [form, setForm] = useState({
    title: "",
    short_description: "",
    description: "",
    category_id: "",
    level: "beginner",
    language: "العربية",
    price: "",
    faqs: [{ question: "", answer: "" }],
    requirements: [""],
    outcomes: [""],
    thumbnail: null,
  });

  const [selectedParent, setSelectedParent] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // تحميل الفئات
  useEffect(() => {
    const load = async () => {
      try {
        const data = await fetchCategories();
        setCategories(data);
      } catch (e) {
        console.error(e);
      }
    };
    load();
  }, []);

  // عند اختيار فئة رئيسية
  const handleParentChange = (parentId) => {
    setSelectedParent(parentId);
    const parent = categories.find((c) => c.id === Number(parentId));
    const children = parent?.children || [];
    setSubCategories(children);

    if (children.length === 0) {
      setForm((prev) => ({ ...prev, category_id: parentId }));
    } else {
      setForm((prev) => ({ ...prev, category_id: "" }));
    }
  };

  // معالجة رفع الصورة
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setForm({ ...form, thumbnail: file });
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    // تنظيف البيانات
    const cleanForm = {
      ...form,
      faqs: form.faqs.filter((f) => f.question.trim() !== "" && f.answer.trim() !== ""),
      requirements: form.requirements.filter((r) => r.trim() !== ""),
      outcomes: form.outcomes.filter((o) => o.trim() !== ""),
      price: parseFloat(form.price) || 0,
    };

    // التحقق من الحقول المطلوبة
    if (!cleanForm.title || !cleanForm.short_description || !cleanForm.description || !cleanForm.category_id) {
      setError("الرجاء ملء جميع الحقول المطلوبة");
      setLoading(false);
      return;
    }

    try {
      await createTrainerCourse(cleanForm);
      setSuccess("تم إنشاء الكورس بنجاح ✅");
      
      // إعادة تعيين النموذج
      setForm({
        title: "",
        short_description: "",
        description: "",
        category_id: "",
        level: "beginner",
        language: "العربية",
        price: "",
        faqs: [{ question: "", answer: "" }],
        requirements: [""],
        outcomes: [""],
        thumbnail: null,
      });
      setSelectedParent("");
      setSubCategories([]);
      setImagePreview(null);
    } catch (e) {
      console.error(e);
      setError("فشل إنشاء الكورس. تأكد من صحة البيانات");
    } finally {
      setLoading(false);
    }
  };

  // إضافة حقل جديد للمتطلبات
  const addRequirement = () => {
    setForm({
      ...form,
      requirements: [...form.requirements, ""],
    });
  };

  // إضافة حقل جديد للنتائج
  const addOutcome = () => {
    setForm({
      ...form,
      outcomes: [...form.outcomes, ""],
    });
  };

  // إضافة سؤال جديد
  const addFAQ = () => {
    setForm({
      ...form,
      faqs: [...form.faqs, { question: "", answer: "" }],
    });
  };

  // إزالة حقل
  const removeField = (field, index) => {
    if (field === 'requirements') {
      if (form.requirements.length > 1) {
        const newRequirements = form.requirements.filter((_, i) => i !== index);
        setForm({ ...form, requirements: newRequirements });
      }
    } else if (field === 'outcomes') {
      if (form.outcomes.length > 1) {
        const newOutcomes = form.outcomes.filter((_, i) => i !== index);
        setForm({ ...form, outcomes: newOutcomes });
      }
    } else if (field === 'faqs') {
      if (form.faqs.length > 1) {
        const newFaqs = form.faqs.filter((_, i) => i !== index);
        setForm({ ...form, faqs: newFaqs });
      }
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.pageTitle}>إنشاء كورس جديد</h1>
        <p style={styles.pageSubtitle}>املأ التفاصيل لإنشاء كورس احترافي</p>
      </div>

      <div style={styles.content}>
        {success && (
          <div style={styles.successMessage}>
            <span style={styles.successIcon}>✅</span>
            {success}
          </div>
        )}
        
        {error && (
          <div style={styles.errorMessage}>
            <span style={styles.errorIcon}>⚠️</span>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} style={styles.form}>
          {/* المعلومات الأساسية */}
          <div style={styles.sectionCard}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>📝 المعلومات الأساسية</h2>
              <span style={styles.sectionRequired}>* مطلوب</span>
            </div>

            <div style={styles.formGrid}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>
                  <span style={styles.required}>*</span> عنوان الكورس
                </label>
                <input
                  placeholder="مثال: تطوير تطبيقات ويب باستخدام React"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  required
                  style={styles.input}
                />
                <small style={styles.helpText}>يجب أن يكون العنوان جذاباً وواضحاً</small>
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>
                  <span style={styles.required}>*</span> المستوى
                </label>
                <select
                  value={form.level}
                  onChange={(e) => setForm({ ...form, level: e.target.value })}
                  style={styles.select}
                >
                  <option value="beginner">مبتدئ</option>
                  <option value="intermediate">متوسط</option>
                  <option value="advanced">متقدم</option>
                  <option value="all">جميع المستويات</option>
                </select>
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>
                  <span style={styles.required}>*</span> اللغة
                </label>
                <select
                  value={form.language}
                  onChange={(e) => setForm({ ...form, language: e.target.value })}
                  style={styles.select}
                >
                  <option value="العربية">العربية</option>
                  <option value="الإنجليزية">الإنجليزية</option>
                  <option value="كلاهما">العربية والإنجليزية</option>
                </select>
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>
                  <span style={styles.required}>*</span> السعر (ريال)
                </label>
                <div style={styles.priceInput}>
                  <input
                    type="number"
                    placeholder="0"
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                    required
                    style={styles.input}
                    min="0"
                    step="0.01"
                  />
                  <span style={styles.currency}>ر.س</span>
                </div>
                <small style={styles.helpText}>أدخل 0 إذا كان الكورس مجانياً</small>
              </div>
            </div>

            <div style={styles.textareaGroup}>
              <label style={styles.label}>
                <span style={styles.required}>*</span> الوصف المختصر
              </label>
              <textarea
                placeholder="وصف مختصر يظهر في صفحة العرض الرئيسية..."
                value={form.short_description}
                onChange={(e) => setForm({ ...form, short_description: e.target.value })}
                required
                style={styles.textarea}
                rows="3"
                maxLength="200"
              />
              <small style={styles.helpText}>{form.short_description.length}/200 حرف</small>
            </div>

            <div style={styles.textareaGroup}>
              <label style={styles.label}>
                <span style={styles.required}>*</span> الوصف الكامل
              </label>
              <textarea
                placeholder="صف الكورس بتفصيل، ما الذي يقدمه، لمن هو موجه، وما الفائدة منه..."
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                required
                style={styles.textarea}
                rows="6"
              />
            </div>
          </div>

          {/* الفئة */}
          <div style={styles.sectionCard}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>📂 الفئة</h2>
            </div>

            <div style={styles.categoryGrid}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>
                  <span style={styles.required}>*</span> الفئة الرئيسية
                </label>
                <select
                  value={selectedParent}
                  onChange={(e) => handleParentChange(e.target.value)}
                  required
                  style={styles.select}
                >
                  <option value="">اختر الفئة الرئيسية</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              {subCategories.length > 0 && (
                <div style={styles.inputGroup}>
                  <label style={styles.label}>
                    <span style={styles.required}>*</span> الفئة الفرعية
                  </label>
                  <select
                    value={form.category_id}
                    onChange={(e) => setForm({ ...form, category_id: e.target.value })}
                    required
                    style={styles.select}
                  >
                    <option value="">اختر الفئة الفرعية</option>
                    {subCategories.map((sc) => (
                      <option key={sc.id} value={sc.id}>
                        {sc.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* صورة الغلاف */}
          <div style={styles.sectionCard}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>🖼️ صورة الغلاف</h2>
            </div>

            <div style={styles.imageUpload}>
              <div style={styles.imagePreview}>
                {imagePreview ? (
                  <img src={imagePreview} alt="Preview" style={styles.previewImage} />
                ) : (
                  <div style={styles.placeholderImage}>
                    <span style={styles.placeholderIcon}>📷</span>
                    <span style={styles.placeholderText}>صورة الكورس</span>
                  </div>
                )}
              </div>
              <div style={styles.uploadControls}>
                <label style={styles.uploadButton}>
                  <span>📁 اختر صورة</span>
                  <input
                    type="file"
                    onChange={handleImageChange}
                    accept="image/*"
                    style={styles.fileInput}
                  />
                </label>
                <p style={styles.uploadHelp}>
                  المقاسات الموصى بها: 1280×720 بكسل<br />
                  الصيغ المقبولة: JPG, PNG, GIF
                </p>
              </div>
            </div>
          </div>

          {/* المتطلبات */}
          <div style={styles.sectionCard}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>📋 متطلبات الكورس</h2>
            </div>

            <div style={styles.dynamicFields}>
              {form.requirements.map((r, i) => (
                <div key={i} style={styles.dynamicField}>
                  <input
                    placeholder={`متطلب ${i + 1} (مثال: معرفة أساسية بالبرمجة)`}
                    value={r}
                    onChange={(e) => {
                      const arr = [...form.requirements];
                      arr[i] = e.target.value;
                      setForm({ ...form, requirements: arr });
                    }}
                    style={styles.input}
                  />
                  {form.requirements.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeField('requirements', i)}
                      style={styles.removeButton}
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
              <button type="button" onClick={addRequirement} style={styles.addButton}>
                + إضافة متطلب
              </button>
            </div>
          </div>

          {/* النتائج المتوقعة */}
          <div style={styles.sectionCard}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>🎯 ماذا سيتعلم الطالب</h2>
            </div>

            <div style={styles.dynamicFields}>
              {form.outcomes.map((o, i) => (
                <div key={i} style={styles.dynamicField}>
                  <input
                    placeholder={`نتيجة ${i + 1} (مثال: سيتمكن من بناء تطبيق ويب كامل)`}
                    value={o}
                    onChange={(e) => {
                      const arr = [...form.outcomes];
                      arr[i] = e.target.value;
                      setForm({ ...form, outcomes: arr });
                    }}
                    style={styles.input}
                  />
                  {form.outcomes.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeField('outcomes', i)}
                      style={styles.removeButton}
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
              <button type="button" onClick={addOutcome} style={styles.addButton}>
                + إضافة نتيجة
              </button>
            </div>
          </div>

          {/* الأسئلة الشائعة */}
          <div style={styles.sectionCard}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>❓ الأسئلة الشائعة</h2>
            </div>

            <div style={styles.faqSection}>
              {form.faqs.map((faq, i) => (
                <div key={i} style={styles.faqItem}>
                  <div style={styles.faqHeader}>
                    <h4 style={styles.faqNumber}>سؤال {i + 1}</h4>
                    {form.faqs.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeField('faqs', i)}
                        style={styles.removeButton}
                      >
                        ✕ حذف
                      </button>
                    )}
                  </div>
                  <input
                    placeholder="السؤال (مثال: هل أحتاج لخبرة مسبقة؟)"
                    value={faq.question}
                    onChange={(e) => {
                      const arr = [...form.faqs];
                      arr[i].question = e.target.value;
                      setForm({ ...form, faqs: arr });
                    }}
                    style={styles.input}
                  />
                  <textarea
                    placeholder="الجواب (مثال: لا، الكورس مصمم للمبتدئين...)"
                    value={faq.answer}
                    onChange={(e) => {
                      const arr = [...form.faqs];
                      arr[i].answer = e.target.value;
                      setForm({ ...form, faqs: arr });
                    }}
                    style={styles.textarea}
                    rows="3"
                  />
                </div>
              ))}
              <button type="button" onClick={addFAQ} style={styles.addButton}>
                + إضافة سؤال
              </button>
            </div>
          </div>

          {/* زر الإرسال */}
          <div style={styles.submitSection}>
            <button type="submit" disabled={loading} style={loading ? { ...styles.submitButton, ...styles.submitButtonLoading } : styles.submitButton}>
              {loading ? (
                <>
                  <span style={styles.spinner}></span>
                  جاري إنشاء الكورس...
                </>
              ) : (
                "🚀 إنشاء الكورس"
              )}
            </button>
            <p style={styles.submitNote}>
              ستتم مراجعة الكورس من قبل الإدارة قبل النشر
            </p>
          </div>
        </form>
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

const styles = {
  container: {
    direction: 'rtl',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    background: '#f5f7fa',
    minHeight: '100vh',
  },
  header: {
    background: 'linear-gradient(135deg, #2c3e50 0%, #1a2530 100%)',
    color: 'white',
    padding: '30px',
    borderRadius: '0 0 15px 15px',
    marginBottom: '30px',
  },
  pageTitle: {
    fontSize: '2rem',
    margin: '0 0 10px 0',
    fontWeight: '700',
  },
  pageSubtitle: {
    fontSize: '1rem',
    opacity: '0.9',
    margin: 0,
  },
  content: {
    maxWidth: '1000px',
    margin: '0 auto',
    padding: '0 20px 40px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '25px',
  },
  sectionCard: {
    background: 'white',
    borderRadius: '15px',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
    padding: '30px',
    animation: 'fadeIn 0.5s ease-out',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '25px',
    paddingBottom: '15px',
    borderBottom: '2px solid #f0f0f0',
  },
  sectionTitle: {
    fontSize: '1.4rem',
    margin: 0,
    color: '#333',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  sectionRequired: {
    fontSize: '0.9rem',
    color: '#f44336',
    fontWeight: '500',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '20px',
    marginBottom: '25px',
  },
  categoryGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px',
  },
  inputGroup: {
    marginBottom: '15px',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '600',
    color: '#555',
    fontSize: '0.95rem',
  },
  required: {
    color: '#f44336',
    marginLeft: '4px',
  },
  input: {
    width: '100%',
    padding: '12px 15px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '1rem',
    transition: 'all 0.3s ease',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
  select: {
    width: '100%',
    padding: '12px 15px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '1rem',
    backgroundColor: 'white',
    fontFamily: 'inherit',
    cursor: 'pointer',
  },
  priceInput: {
    position: 'relative',
  },
  currency: {
    position: 'absolute',
    left: '15px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#666',
  },
  helpText: {
    display: 'block',
    marginTop: '6px',
    color: '#777',
    fontSize: '0.85rem',
  },
  textareaGroup: {
    marginBottom: '20px',
  },
  textarea: {
    width: '100%',
    padding: '12px 15px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '1rem',
    transition: 'all 0.3s ease',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    resize: 'vertical',
  },
  imageUpload: {
    display: 'flex',
    gap: '30px',
    alignItems: 'flex-start',
  },
  imagePreview: {
    width: '250px',
    height: '140px',
    borderRadius: '10px',
    overflow: 'hidden',
    border: '2px dashed #e0e0e0',
    background: '#f8f9fa',
    flexShrink: 0,
  },
  previewImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  placeholderImage: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#999',
  },
  placeholderIcon: {
    fontSize: '2.5rem',
    marginBottom: '10px',
  },
  placeholderText: {
    fontSize: '0.9rem',
  },
  uploadControls: {
    flex: 1,
  },
  uploadButton: {
    display: 'inline-block',
    padding: '12px 25px',
    background: '#667eea',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    marginBottom: '15px',
  },
  fileInput: {
    display: 'none',
  },
  uploadHelp: {
    fontSize: '0.9rem',
    color: '#666',
    lineHeight: '1.5',
    margin: 0,
  },
  dynamicFields: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
  },
  dynamicField: {
    display: 'flex',
    gap: '10px',
    alignItems: 'center',
  },
  removeButton: {
    padding: '8px 12px',
    background: '#ffebee',
    color: '#f44336',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: '500',
    transition: 'all 0.3s ease',
    flexShrink: 0,
  },
  addButton: {
    padding: '10px 20px',
    background: '#f5f7fa',
    color: '#667eea',
    border: '2px dashed #667eea',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    textAlign: 'center',
    alignSelf: 'flex-start',
  },
  faqSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '25px',
  },
  faqItem: {
    padding: '20px',
    border: '1px solid #f0f0f0',
    borderRadius: '10px',
    background: '#f8f9fa',
  },
  faqHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '15px',
  },
  faqNumber: {
    margin: 0,
    color: '#333',
    fontSize: '1.1rem',
    fontWeight: '600',
  },
  submitSection: {
    textAlign: 'center',
    marginTop: '20px',
  },
  submitButton: {
    padding: '16px 40px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontSize: '1.2rem',
    fontWeight: '700',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    margin: '0 auto 15px',
  },
  submitButtonLoading: {
    opacity: '0.8',
    cursor: 'not-allowed',
  },
  spinner: {
    width: '20px',
    height: '20px',
    border: '3px solid rgba(255, 255, 255, 0.3)',
    borderRadius: '50%',
    borderTopColor: 'white',
    animation: 'spin 1s ease-in-out infinite',
  },
  submitNote: {
    fontSize: '0.9rem',
    color: '#666',
    margin: 0,
  },
  successMessage: {
    background: '#e8f5e9',
    color: '#2e7d32',
    padding: '15px 20px',
    borderRadius: '8px',
    marginBottom: '25px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    borderRight: '4px solid #4CAF50',
    animation: 'fadeIn 0.3s ease-out',
  },
  successIcon: {
    fontSize: '1.5rem',
  },
  errorMessage: {
    background: '#ffebee',
    color: '#c62828',
    padding: '15px 20px',
    borderRadius: '8px',
    marginBottom: '25px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    borderRight: '4px solid #f44336',
    animation: 'fadeIn 0.3s ease-out',
  },
  errorIcon: {
    fontSize: '1.5rem',
  },
};

// إضافة تأثيرات Hover
if (typeof window !== 'undefined') {
  setTimeout(() => {
    const style = document.createElement('style');
    style.textContent = `
      .input-focus:focus, .select-focus:focus, .textarea-focus:focus {
        outline: none;
        border-color: #667eea !important;
        box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1) !important;
      }
      
      .upload-btn-hover:hover {
        background: #764ba2 !important;
        transform: translateY(-2px);
        box-shadow: 0 5px 15px rgba(102, 126, 234, 0.3) !important;
      }
      
      .remove-btn-hover:hover {
        background: #f44336 !important;
        color: white !important;
      }
      
      .add-btn-hover:hover {
        background: #e8eaff !important;
        border-style: solid !important;
      }
      
      .submit-btn-hover:hover:not(:disabled) {
        transform: translateY(-3px);
        box-shadow: 0 10px 25px rgba(102, 126, 234, 0.4) !important;
      }
      
      @media (max-width: 768px) {
        .image-upload-responsive {
          flex-direction: column !important;
          align-items: center !important;
          text-align: center !important;
        }
        
        .category-grid-responsive {
          grid-template-columns: 1fr !important;
        }
        
        .form-grid-responsive {
          grid-template-columns: 1fr !important;
        }
      }
      
      @media (max-width: 480px) {
        .dynamic-field-responsive {
          flex-direction: column !important;
          align-items: stretch !important;
        }
        
        .remove-btn-responsive {
          align-self: flex-start !important;
        }
      }
    `;
    document.head.appendChild(style);
    
    // إضافة classes للعناصر
    setTimeout(() => {
      document.querySelectorAll('input').forEach(el => {
        el.className = (el.className || '') + ' input-focus';
      });
      document.querySelectorAll('select').forEach(el => {
        el.className = (el.className || '') + ' select-focus';
      });
      document.querySelectorAll('textarea').forEach(el => {
        el.className = (el.className || '') + ' textarea-focus';
      });
      document.querySelectorAll('[style*="uploadButton"]').forEach(el => {
        el.className = (el.className || '') + ' upload-btn-hover';
      });
      document.querySelectorAll('[style*="removeButton"]').forEach(el => {
        el.className = (el.className || '') + ' remove-btn-hover';
      });
      document.querySelectorAll('[style*="addButton"]').forEach(el => {
        el.className = (el.className || '') + ' add-btn-hover';
      });
      document.querySelectorAll('[style*="submitButton"]').forEach(el => {
        el.className = (el.className || '') + ' submit-btn-hover';
      });
      
      // Responsive classes
      if (window.innerWidth <= 768) {
        const imageUpload = document.querySelector('[style*="imageUpload"]');
        const categoryGrid = document.querySelector('[style*="categoryGrid"]');
        const formGrid = document.querySelector('[style*="formGrid"]');
        if (imageUpload) imageUpload.className = (imageUpload.className || '') + ' image-upload-responsive';
        if (categoryGrid) categoryGrid.className = (categoryGrid.className || '') + ' category-grid-responsive';
        if (formGrid) formGrid.className = (formGrid.className || '') + ' form-grid-responsive';
      }
      if (window.innerWidth <= 480) {
        const dynamicFields = document.querySelectorAll('[style*="dynamicField"]');
        dynamicFields.forEach(el => {
          el.className = (el.className || '') + ' dynamic-field-responsive';
        });
        const removeButtons = document.querySelectorAll('[style*="removeButton"]');
        removeButtons.forEach(el => {
          el.className = (el.className || '') + ' remove-btn-responsive';
        });
      }
    }, 200);
  }, 100);
}